import type {
  DatabaseAccessGrant,
  GetDatabaseAccessGrantsResponse
} from '@gat-ship/types'
import { inject, onBeforeUnmount, readonly, ref, shallowRef } from 'vue'
import {
  tenantUserDatabaseAccessGrantsKey,
  type TenantUserDatabaseAccessGrantsService
} from '@gat-ship/types/injection-keys'
import { useTenantUserSignalREvents } from '@gat-ship/signalr'
import { globalUtils } from '@gat-ship/utils'
import { useGsCloudBackend } from '@gat-ship/http-client'

export function useTenantUserDatabaseAccessGrants(): TenantUserDatabaseAccessGrantsService {
  const { apiGet } = useGsCloudBackend()
  /**
   * The ID of the tenant user that the data is for.
   * This is used to filter the data to only the tenant user that the data is for.
   * This is also used to subscribe to the correct tenant user events.
   */
  const _tenantUserId = ref<string | undefined>(undefined)

  /**
   * The database access grants for the tenant user.
   */
  const databaseAccessGrants = shallowRef<DatabaseAccessGrant[]>([])

  function setDbAccessGrants(accessGrants: DatabaseAccessGrant[]) {
    databaseAccessGrants.value = accessGrants
  }

  function updateDatabaseAccessGrant(
    grantId: string,
    updatedGrant: DatabaseAccessGrant
  ) {
    const accessGrants = [...databaseAccessGrants.value]
    // Find by grantId instead of databaseId to ensure we update the correct grant
    const index = accessGrants.findIndex((i) => i.grantId === grantId)
    if (index !== -1) {
      accessGrants[index] = updatedGrant
      setDbAccessGrants(accessGrants)
    }
  }

  function _setTenantUserId(tenantUserId: string | undefined) {
    _tenantUserId.value = tenantUserId
  }

  function addDatabaseAccessGrant(grant: DatabaseAccessGrant) {
    setDbAccessGrants([...databaseAccessGrants.value, grant])
  }

  function removeDatabaseAccessGrant(grantId: string) {
    setDbAccessGrants(
      [...databaseAccessGrants.value].filter((i) => i.grantId !== grantId)
    )
  }

  const tenantUserEvents = useTenantUserSignalREvents({
    onDatabaseAccessGranted: (event) => {
      if (event.message.userId !== _tenantUserId.value) {
        return
      }
      addDatabaseAccessGrant({
        databaseId: event.message.databaseId,
        databaseName: event.message.databaseDisplayName,
        grantId: event.message.grantId,
        tenantId: event.message.tenantId,
        userId: event.message.userId,
        grantedAt: globalUtils.functions.dateNow(),
        grantedBy: event.message.userId
      })
    },
    onDatabaseAccessRevoked: (event) => {
      if (event.message.userId !== _tenantUserId.value) {
        return
      }
      removeDatabaseAccessGrant(event.message.grantId)
    }
  })

  function subscribeToDatabaseAccessEvents(tenantUserId: string) {
    _setTenantUserId(tenantUserId)
    tenantUserEvents.subscribeToTenantUserEvents()
    return () => {
      tenantUserEvents.unsubscribeFromTenantUserEvents()
    }
  }

  function unsubscribeFromDatabaseAccessEvents() {
    _setTenantUserId(undefined)
    tenantUserEvents.unsubscribeFromTenantUserEvents()
  }

  /**
   * Fetches the database access grants and owned databases for a tenant user.
   * @param tenantUserId - The ID of the tenant user to fetch the data for.
   * @param options - Optional options for the fetch.
   * @param options.forceFetch - Whether to force a fetch of the data. @default false
   * @param options.subscribeToEvents - Whether to subscribe to events. @default true
   * @returns The database access grants and owned databases for the tenant user.
   */
  async function fetchData(
    tenantUserId: string,
    options?: { forceFetch?: boolean; subscribeToEvents?: boolean }
  ) {
    const promises: Promise<void>[] = []

    const isTenantUserChanged = tenantUserId !== _tenantUserId.value
    if (isTenantUserChanged || options?.forceFetch) {
      promises.push(
        apiGet<GetDatabaseAccessGrantsResponse>(
          `/access-control/users/${tenantUserId}/database-grants`
        ).then((res) => setDbAccessGrants(res.databaseGrants))
      )
    }
    await Promise.all(promises)

    if (options?.subscribeToEvents ?? true) {
      subscribeToDatabaseAccessEvents(tenantUserId)
    }
    _setTenantUserId(tenantUserId)
    return {
      databaseAccessGrants: readonly(databaseAccessGrants.value)
    }
  }

  onBeforeUnmount(() => {
    unsubscribeFromDatabaseAccessEvents()
  })

  return {
    databaseAccessGrants,
    fetchData,
    addDatabaseAccessGrant,
    updateDatabaseAccessGrant,
    removeDatabaseAccessGrant,
    subscribeToDatabaseAccessEvents,
    unsubscribeFromDatabaseAccessEvents,
    /**
     * Clears the data and unsubscribes from events.
     * @param options - Optional options for the clear.
     * @param options.unsubscribeFromEvents - Whether to unsubscribe from events. @default true
     */
    clearData: (options?: { unsubscribeFromEvents?: boolean }) => {
      _setTenantUserId(undefined)
      setDbAccessGrants([])
      if (options?.unsubscribeFromEvents ?? true) {
        unsubscribeFromDatabaseAccessEvents()
      }
    }
  }
}

/** Inject the tenant-user database-access-grants context provided by a parent provider. */
export function useTenantUserDatabaseAccessGrantsContext() {
  return inject(tenantUserDatabaseAccessGrantsKey, undefined)
}
